// Shared, dependency-light implementation of "how many minutes of task T is person P
// contributing on day D" (actual/remaining-effort mode). Every function here is pure: it reads
// only its arguments (no browser globals, no feature-flag lookups, no DataManager access), so it
// can be evaluated from any runtime, not just the client. Feature-flag values that used to be read
// directly inside these functions are now plain boolean parameters — the caller resolves the flag
// and passes the result in.
//
// The one exception is the import below: `getNonWorkingDaysCount` is still pulled in from the
// original NonWorkingDaysLogic module rather than duplicated here, because that function is not
// pure (it reads a feature flag internally) and porting it is out of scope for this module. It is
// only reached on the legacy (flag-off) branch of processTaskActualMode below.
import {getNonWorkingDaysCount, getNonWorkingDaysDistribution} from '../NonWorkingDaysLogic';

// ---- Canvas timeline date encoding ----
// A "canvas timeline date" is an integer day count from a fixed epoch (year 2000). Encoding via
// Date.UTC keeps this independent of the runtime's local timezone.
//
// The constants and helpers below (the epoch math, the Monday-first-week offset, the weekday
// index enum, and the two encoding functions) are deliberate duplicates of equivalent logic that
// already exists elsewhere for the interactive canvas timeline. They are duplicated here rather
// than imported, so this module has no import edges into the canvas-timeline/DataManager graph.
// They are not a second source of truth to independently maintain -- if the canonical encoding
// ever changes, this copy needs to change in step with it.

const MIN_DATE_YEAR = 2000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MIN_DATE_YEAR_IN_MS = new Date(Date.UTC(MIN_DATE_YEAR, 0, 1)).getTime();

export const getCanvasTimelineDate = (year, month, day) => {
	const date = new Date(Date.UTC(year, month - 1, day));
	return Math.floor((date.getTime() - MIN_DATE_YEAR_IN_MS) / MS_PER_DAY);
};

const canvasTimelineDateCache = new Map();

// Same encoding as getCanvasTimelineDate, memoized by (year, month, day).
export const createCanvasTimelineDate = (year, month, day) => {
	let yearCache = canvasTimelineDateCache.get(year);
	if (!yearCache) {
		yearCache = new Map();
		canvasTimelineDateCache.set(year, yearCache);
	}
	let monthCache = yearCache.get(month);
	if (!monthCache) {
		monthCache = new Map();
		yearCache.set(month, monthCache);
	}
	let canvasTimelineDate = monthCache.get(day);
	if (canvasTimelineDate === undefined) {
		canvasTimelineDate = getCanvasTimelineDate(year, month, day);
		monthCache.set(day, canvasTimelineDate);
	}
	return canvasTimelineDate;
};

// Days-since-epoch offset used to align canvas timeline date 0 onto a Monday-first week.
const CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK = 2;

// The weekday-index formula itself is also a deliberate duplicate of an equivalent inline
// calculation used elsewhere -- kept as a copy here rather than a shared import, for the same
// import-isolation reason as the rest of this section.
const getWeekDayIndexFromCanvasDate = canvasTimelineDate => {
	return (canvasTimelineDate - CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK) % 7;
};

const DAY_INDEX = {
	MONDAY: 0,
	TUESDAY: 1,
	WEDNESDAY: 2,
	THURSDAY: 3,
	FRIDAY: 4,
	SATURDAY: 5,
	SUNDAY: 6,
};

// True when the person is scheduled to work any minutes on the weekday of canvasTimelineDate.
export const isWorkingDay = (personWorkingMinutes, canvasTimelineDate) => {
	const weekdayIndex = getWeekDayIndexFromCanvasDate(canvasTimelineDate);
	return personWorkingMinutes[weekdayIndex] > 0;
};

// ---- Working-day math ----

// Working days left after removing day-off (time-off / holiday) days. Never negative.
export const getWorkingDayCount = (workingDaysInRange, dayOffDaysInRange) =>
	Math.max(0, workingDaysInRange - dayOffDaysInRange);

// ---- Weekday counting between two canvas timeline dates ----
// The four helpers in this section (the weeks-diff calculation, the ISO-weekday calculation, the
// per-weekday counting helper, and getWeekdaysBetweenDatesNew itself) are deliberate duplicates of
// equivalent allocation-counting logic that already exists elsewhere. Duplicated here rather than
// imported, so this module has no import edges into the canvas-timeline/DataManager graph -- not a
// second source of truth to independently maintain, just a copy that needs to move in step with
// the original if that logic ever changes.

const getWeeksDiffFromCanvasTimelineDates = (startDate, endDate) => {
	return Math.floor(Math.abs(endDate - startDate) / 7);
};

const getIsoWeekdayFromCanvasTimelineDate = canvasTimelineDate => {
	return ((canvasTimelineDate - CANVAS_DATE_DAYS_OFFSET_FIRST_WEEK) % 7) + 1;
};

const getWeekdaysOnDay = (startWeekday, endWeekday, dayOfWeekValue, fullWeeksBetween) => {
	const additionalWeekday =
		startWeekday <= endWeekday
			? dayOfWeekValue >= startWeekday && dayOfWeekValue <= endWeekday
			: dayOfWeekValue >= startWeekday || dayOfWeekValue <= endWeekday;

	return (additionalWeekday ? 1 : 0) + fullWeeksBetween;
};

const WEEKDAYS_BETWEEN_DATES_CACHE = new Map();

// Returns a 7-entry array (Monday..Sunday) of how many of each weekday fall within
// [startDate, endDate], optionally subtracting a person's non-working days when
// nonWorkingDaysMap/personId are supplied.
//
// Only pure at the 2-arg call sites used below: passing nonWorkingDaysMap/personId routes through
// getNonWorkingDaysDistribution, which reads a feature flag internally. Callers in this module
// always use the 2-arg form.
export const getWeekdaysBetweenDatesNew = (startDate, endDate, nonWorkingDaysMap, personId) => {
	if (endDate - startDate < 0) {
		return [0, 0, 0, 0, 0, 0, 0];
	}

	const key = `${startDate}-${endDate}`;
	let weekdaysBetween = WEEKDAYS_BETWEEN_DATES_CACHE.get(key);

	if (!weekdaysBetween) {
		weekdaysBetween = [0, 0, 0, 0, 0, 0, 0];

		const fullWeeksBetween = getWeeksDiffFromCanvasTimelineDates(startDate, endDate);
		const startWeekday = getIsoWeekdayFromCanvasTimelineDate(startDate);
		const endWeekday = getIsoWeekdayFromCanvasTimelineDate(endDate);

		for (let dayOfWeekValue = 1; dayOfWeekValue <= 7; dayOfWeekValue++) {
			weekdaysBetween[dayOfWeekValue - 1] = getWeekdaysOnDay(startWeekday, endWeekday, dayOfWeekValue, fullWeeksBetween);
		}

		WEEKDAYS_BETWEEN_DATES_CACHE.set(key, weekdaysBetween);
	}

	let nonWorkingDaysDistribution = [0, 0, 0, 0, 0, 0, 0];
	if (nonWorkingDaysMap && personId) {
		nonWorkingDaysDistribution = getNonWorkingDaysDistribution(startDate, endDate, nonWorkingDaysMap, personId);
	}

	return [
		weekdaysBetween[DAY_INDEX.MONDAY] - nonWorkingDaysDistribution[DAY_INDEX.MONDAY],
		weekdaysBetween[DAY_INDEX.TUESDAY] - nonWorkingDaysDistribution[DAY_INDEX.TUESDAY],
		weekdaysBetween[DAY_INDEX.WEDNESDAY] - nonWorkingDaysDistribution[DAY_INDEX.WEDNESDAY],
		weekdaysBetween[DAY_INDEX.THURSDAY] - nonWorkingDaysDistribution[DAY_INDEX.THURSDAY],
		weekdaysBetween[DAY_INDEX.FRIDAY] - nonWorkingDaysDistribution[DAY_INDEX.FRIDAY],
		weekdaysBetween[DAY_INDEX.SATURDAY] - nonWorkingDaysDistribution[DAY_INDEX.SATURDAY],
		weekdaysBetween[DAY_INDEX.SUNDAY] - nonWorkingDaysDistribution[DAY_INDEX.SUNDAY],
	];
};

// ---- Distribution map accumulation ----
// addToMap and addActualTaskMinutesToMap are deliberate duplicates of equivalent heatmap
// distribution-map accumulation logic that already exists elsewhere. Duplicated here rather than
// imported, so this module has no import edges into the canvas-timeline/DataManager graph -- not a
// second source of truth to independently maintain, just a copy that needs to move in step with
// the original if that logic ever changes.

const addToMap = (map, id, cb) => {
	const existingItem = map.get(id);
	const item = existingItem || {
		minutes: 0,
		minutesWin: 0,
		minutesHard: 0,
		minutesSoft: 0,
		minutesSoftWin: 0,
		actualTaskMinutes: 0,
		plannedTaskMinutes: 0,
		timeRegMinutes: 0,
		allocationMinutes: 0,
		plannedAllocationMinutes: 0,
		plannedAllocationMinutesWin: 0,
		plannedAllocationMinutesHard: 0,
		plannedAllocationMinutesSoft: 0,
		plannedAllocationMinutesSoftWin: 0,
	};
	cb(item);
	if (!existingItem) {
		map.set(id, item);
	}
};

const addActualTaskMinutesToMap = (map, id, taskMinutes) => {
	addToMap(map, id, item => {
		item.minutes += taskMinutes;
		item.minutesWin += taskMinutes;
		item.minutesHard += taskMinutes;
		item.actualTaskMinutes += taskMinutes;
	});
};

// ---- Person working minutes ----

export const getPersonWorkingHoursAndMinutes = person => {
	const personWorkingHours = person.workingHours || {
		monday: person.monday,
		tuesday: person.tuesday,
		wednesday: person.wednesday,
		thursday: person.thursday,
		friday: person.friday,
		saturday: person.saturday,
		sunday: person.sunday,
	};

	const personWorkingMinutes = [
		personWorkingHours.monday,
		personWorkingHours.tuesday,
		personWorkingHours.wednesday,
		personWorkingHours.thursday,
		personWorkingHours.friday,
		personWorkingHours.saturday,
		personWorkingHours.sunday,
	];

	return {personWorkingHours, personWorkingMinutes};
};

// Sum only Monday (0) through Friday (4)
const countWeekdaysOnly = weekdays => {
	return weekdays.slice(0, 5).reduce((total, count) => total + count, 0);
};

// Sum only Saturday (5) and Sunday (6)
const countWeekendOnly = weekdays => {
	return weekdays[5] + weekdays[6];
};

// Sum the task's day counts over every day the person is scheduled to work at all -- all seven
// index positions, not just Mon-Fri, so a Saturday worker's weekend days count here too. That is
// deliberate: the main branch's own working-day tally (taskWorkingDayCount / daysWithinCurrentLoop
// in processTaskActualMode and HeatmapLogic's processTask) is the same all-seven reduce, so
// numerator and denominator stay in one domain and the per-period allocations still sum to
// timeLeft. Returns 0 when the person works none of the days the task spans (including a zero-hour
// working week, or no personWorkingMinutes).
const countPersonWorkingDays = (weekdays, personWorkingMinutes) => {
	return weekdays.reduce((total, weekdayCount, index) => {
		return total + (personWorkingMinutes?.[index] ? weekdayCount : 0);
	}, 0);
};

// Fallback for when the person has no *available* working day across the task's span -- either the
// task never touches a day they work (a weekend-only task for a Mon-Fri worker), or every working
// day it covers is a day off. The demand still has to land somewhere, or the task disappears from
// the heatmap entirely.
export const calculateTaskMinutesAllocated = (taskTotalWeekdays, taskLoopWeekdays, timeLeft, task, personWorkingMinutes) => {
	// We need to determine whether the task spans only weekends, only weekdays, or both.
	// - If it spans any Mon-Fri day, take the weekday branch below.
	// - If it spans weekends only, distribute across those weekend days.
	// Why? Because it's more accurate to reflect over-allocation on the weekdays as that is when the person is normally expected to work.
	// Note this Mon-Fri test only picks the branch. Within the weekday branch the effort is spread
	// over the days the person actually works, which for a weekend worker includes Sat/Sun; only the
	// even-split fall-through below is restricted to literal Mon-Fri days.

	const weekdaysCount = countWeekdaysOnly(taskTotalWeekdays);
	const weekendDaysCount = countWeekendOnly(taskTotalWeekdays);

	//Total length of task in days
	const taskTotalDayCount = weekdaysCount || weekendDaysCount;

	if (!taskTotalDayCount) return 0;

	const assignedPersonCount = task.assignedPersons.length || 1;

	if (weekdaysCount) {
		// Weekday-spanning task: spread across the days the person normally works -- weekend days
		// included, if they work them -- so the effort lands on the days they were expected to work
		// rather than on every calendar weekday, and a loop period holding none of those days gets
		// nothing.
		//
		// This is what the previous working-day check was reaching for, but it tested the wrong
		// value: it looked the loop period's anchor date up in the day-off set and passed the *result*
		// to isWorkingDay. A SortedSet lookup returns null for a day that is not a day off, so the
		// check was effectively inverted -- it paid out only when that single anchor date happened to
		// be a day off, and allocated nothing the rest of the time.
		const personWorkingDaysTotal = countPersonWorkingDays(taskTotalWeekdays, personWorkingMinutes);

		if (personWorkingDaysTotal > 0) {
			const personWorkingDaysInLoop = countPersonWorkingDays(taskLoopWeekdays, personWorkingMinutes);
			return (timeLeft * personWorkingDaysInLoop) / personWorkingDaysTotal / assignedPersonCount;
		}

		// The person works none of the days this task spans, so there is no working-day pattern to
		// honour -- fall through to an even spread over the task's Mon-Fri days. Two ways to get
		// here: a zero-hour working week, or a person whose only working days are weekend days on a
		// task that spans no weekend (e.g. a Saturday-only worker on a Mon-Fri task).
	}

	//Number of days within current loop period
	const taskLoopTotalDayCount = weekdaysCount ? countWeekdaysOnly(taskLoopWeekdays) : countWeekendOnly(taskLoopWeekdays);

	return (timeLeft * taskLoopTotalDayCount) / taskTotalDayCount / assignedPersonCount;
};

// ---- Holidays ----

export const getHolidayCountInPeriod = (
	holidaysExcludingDaysOff,
	periodStartDate,
	periodEndDate,
	personWorkingMinutes,
	combinedModePerformanceImprovementsEnabled
) => {
	if (personWorkingMinutes && combinedModePerformanceImprovementsEnabled) {
		return holidaysExcludingDaysOff.filter(date => {
			return date >= periodStartDate && date <= periodEndDate && isWorkingDay(personWorkingMinutes, date);
		}).length;
	} else {
		return holidaysExcludingDaysOff.filter(date => {
			return date >= periodStartDate && date <= periodEndDate;
		}).length;
	}
};

// ---- Task actual/remaining-effort mode ----

// How many minutes of a task's remaining (not-yet-registered) effort person `person` is
// contributing on each day of [startDate, endDate] (a loop/step period), returned as an updated
// [minutesAllocated, distributionMap] pair.
export const processTaskActualMode = (
	taskItemData,
	minutesAllocated,
	distributionMap,
	startDate,
	endDate,
	taskStartDate,
	taskEndDate,
	todayDate,
	personWorkingMinutes,
	holidaysExcludingDaysOff,
	nonWorkingDaysMap,
	person,
	isUsingCombinedMode,
	invertedPtoNonWorkingDaysEnabled,
	combinedModePerformanceImprovementsEnabled
) => {
	const task = taskItemData.task;
	if (task.done) return [minutesAllocated, distributionMap];

	const distributionMapId = isUsingCombinedMode ? taskItemData.projectGroupId || task.projectId : task.projectId;

	const timeLeft = task.timeLeftMinutesWithoutFutureTimeRegs;
	const personId = person.id;

	//Peace out if task has no remaining since it will not affect heatmap regardless of its position.
	//`!(timeLeft > 0)`, not `timeLeft <= 0` -- undefined/NaN must also be treated as "nothing left
	//to distribute": `undefined <= 0` is false, which would otherwise let an undefined
	//timeLeftMinutesWithoutFutureTimeRegs flow into the division below and produce a NaN that
	//poisons the whole heatmap column via `minutesAllocated += NaN`.
	if (!(timeLeft > 0)) return [minutesAllocated, distributionMap];

	//Check if task is within current loop period (can get to this point if only a time registration outside of task is within looped period)
	if (!(taskStartDate > endDate || taskEndDate < startDate - 1)) {
		//We do not care about tasks in the past
		const isInThePast = taskEndDate < todayDate;
		if (isInThePast) return [minutesAllocated, distributionMap];

		let taskPersonActualStartDate = taskStartDate;
		if (person.startDate && taskStartDate < person.startDate) {
			taskPersonActualStartDate = person.startDate;
		}

		let taskPersonActualEndDate = taskEndDate;
		if (person.endDate && taskEndDate > person.endDate) {
			taskPersonActualEndDate = person.endDate;
		}

		//If task is partially in the past and partially in the future, use today as the starting date
		const calculationStartDate = Math.max(todayDate, taskPersonActualStartDate);
		const calculationEndDate = taskPersonActualEndDate;

		//How many weekdays are in the task (ex: 1 monday, 1 tuesday, 1 wednesday)
		const taskTotalWeekdays = getWeekdaysBetweenDatesNew(calculationStartDate, calculationEndDate);

		//Weekdays which are within the current loop period
		const taskLoopWeekdays = getWeekdaysBetweenDatesNew(
			Math.max(calculationStartDate, startDate),
			Math.min(calculationEndDate, endDate)
		);

		let taskWorkingDayCount;
		if (invertedPtoNonWorkingDaysEnabled) {
			//Total length of task in days minus days on which the person is supposed to work 0 minutes
			taskWorkingDayCount =
				taskTotalWeekdays.reduce((total, weekdayCount, index) => {
					return total + (personWorkingMinutes[index] ? weekdayCount : 0);
				}, 0) -
				getHolidayCountInPeriod(
					holidaysExcludingDaysOff,
					calculationStartDate,
					calculationEndDate,
					personWorkingMinutes,
					combinedModePerformanceImprovementsEnabled
				);
		} else {
			//Total length of task in days minus days on which the person is supposed to work 0 minutes
			taskWorkingDayCount =
				taskTotalWeekdays.reduce((total, weekdayCount, index) => {
					return total + (personWorkingMinutes[index] ? weekdayCount : 0);
				}, 0) -
				getNonWorkingDaysCount(
					calculationStartDate,
					calculationEndDate,
					nonWorkingDaysMap,
					personId,
					personWorkingMinutes
				);
		}

		//If there are any working days during the task's period, spread timeLeft evenly among working days only
		//If there are no working days during the task's period, spread timeLeft evenly among all days, either to weekdays or weekends
		//Always divide by amount of people assigned
		let taskMinutesAllocated;
		if (taskWorkingDayCount && taskWorkingDayCount > 0) {
			const daysWithinCurrentLoop = taskLoopWeekdays.reduce((total, weekdayCount, index) => {
				return total + (personWorkingMinutes[index] ? weekdayCount : 0);
			}, 0);
			let nonWorkingDaysCount;
			if (invertedPtoNonWorkingDaysEnabled) {
				nonWorkingDaysCount = getHolidayCountInPeriod(
					holidaysExcludingDaysOff,
					Math.max(calculationStartDate, startDate),
					Math.min(calculationEndDate, endDate),
					personWorkingMinutes,
					combinedModePerformanceImprovementsEnabled
				);
			} else {
				nonWorkingDaysCount = getNonWorkingDaysCount(
					Math.max(calculationStartDate, startDate),
					Math.min(calculationEndDate, endDate),
					nonWorkingDaysMap,
					personId,
					personWorkingMinutes
				);
			}

			//Number of days within current loop period minus days on which the person is supposed to work 0 minutes
			const taskLoopWorkingDayCount = getWorkingDayCount(daysWithinCurrentLoop, nonWorkingDaysCount);

			taskMinutesAllocated =
				(timeLeft * taskLoopWorkingDayCount) / taskWorkingDayCount / (task.assignedPersons.length || 1);

			minutesAllocated += taskMinutesAllocated;
		} else {
			taskMinutesAllocated = calculateTaskMinutesAllocated(
				taskTotalWeekdays,
				taskLoopWeekdays,
				timeLeft,
				task,
				personWorkingMinutes
			);
			minutesAllocated += taskMinutesAllocated;
		}
		// Distribution map is not maintained for no-access heatmaps
		if (distributionMap && distributionMapId) {
			addActualTaskMinutesToMap(distributionMap, distributionMapId, taskMinutesAllocated);
		}
	}

	return [minutesAllocated, distributionMap];
};
